import dayjs from 'dayjs'

import decryptJWT from '../utils/decryptJWT'
const getNumber = (data) => data?.replace(/\D/g, '')

export function formatArrayToString(array) {
  return array.join(',')
}

export function formatStringToNumberArray(string) {
  return string.split(',').map(Number)
}

export function formatStringToArray(string) {
  return string.split(',')
}

function formatDate(date, options = {}) {
  const {
    hasHour = false,
    isEndOfDay = false,
    asDate = false,
    format = null
  } = options

  let d = dayjs(date)

  if (isEndOfDay) {
    d = d.endOf('day')
  }

  const defaultFormat = hasHour ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'

  return asDate ? d.toDate() : d.format(format || defaultFormat)
}

export function formatDateToFilter(date) {
  return formatDate(date, { format: 'DD-MM-YYYY' })
}

export function formatDateToLabel(date) {
  return formatDate(date, { format: 'DD/MM/YYYY' })
}
export function formatDateToDatePicker(
  date,
  hasHour = false,
  isEndOfDay = false
) {
  return formatDate(date, { hasHour, isEndOfDay, asDate: true })
}
export function formatDateToGetFilter(
  date,
  hasHour = false,
  isEndOfDay = false
) {
  return formatDate(date, { hasHour, isEndOfDay })
}

export function formatPhone(phone) {
  return getNumber(phone)
}

export function formatCep(cep) {
  return getNumber(cep)
}

export function decrypt(keysToDecrypt) {
  const decryptedParams = keysToDecrypt.reduce((acc, key) => {
    acc[key] = decryptJWT(key)
    return acc
  }, {})
  return decryptedParams
}

export function formatBusca(busca, separadorLinha) {
  const buscaFormatada = busca.trim().replace(/\s{2,}/g, ' ')
  const separadores = {
    enter: '\\n',
    espaco: ' ',
    ',': ',',
    '|': '|'
  }

  const separador = separadores[separadorLinha]
  return separador ? buscaFormatada.replaceAll(separador, ';') : buscaFormatada
}

/**
 * Identifica automaticamente qual separador foi usado na busca
 * e substitui por ponto e vírgula (;) para envio ao backend.
 * 
 * Detecta automaticamente os seguintes separadores (nesta ordem):
 * - Quebra de linha (\n)
 * - Vírgula (,)
 * - Ponto e vírgula (;) - mantém como está
 * - Pipe (|)
 * - Espaço ( )
 * 
 * @param {string} busca - String de busca que pode conter diferentes separadores
 * @returns {string} - String formatada com separador padrão (;)
 * 
 * @example
 * identificarSeparadorAutomatico('123,456,789') // '123;456;789'
 * identificarSeparadorAutomatico('123\n456\n789') // '123;456;789'
 * identificarSeparadorAutomatico('123|456|789') // '123;456;789'
 * identificarSeparadorAutomatico('123 456 789') // '123;456;789'
 */
export function identificarSeparadorAutomatico(busca) {
  if (!busca) return ''
  
  const buscaFormatada = busca.trim().replace(/\s{2,}/g, ' ')
  
  // Identifica qual separador foi usado e substitui por ;
  // Ordem de verificação: quebra de linha, vírgula, ponto e vírgula, pipe, espaço
  if (buscaFormatada.includes('\n')) {
    return buscaFormatada.replaceAll('\n', ';')
  }
  if (buscaFormatada.includes(',')) {
    return buscaFormatada.replaceAll(',', ';')
  }
  if (buscaFormatada.includes(';')) {
    return buscaFormatada
  }
  if (buscaFormatada.includes('|')) {
    return buscaFormatada.replaceAll('|', ';')
  }
  // Se tiver espaços, substitui por ; (espaço funciona como separador)
  if (buscaFormatada.includes(' ')) {
    return buscaFormatada.replaceAll(' ', ';')
  }
  
  return buscaFormatada
}

/**
 * Remove propriedades vazias (strings vazias) de um objeto
 * 
 * @param {Object} obj - Objeto do qual remover propriedades vazias
 * @returns {Object} - Novo objeto sem propriedades vazias
 * 
 * @example
 * removeEmptyProperties({ a: 'valor', b: '', c: 'outro' }) // { a: 'valor', c: 'outro' }
 * removeEmptyProperties({ ULTIMA_CONEXAO_INICIO: '', ULTIMA_CONEXAO_FINAL: '' }) // {}
 */
export function removeEmptyProperties(obj) {
  if (!obj || typeof obj !== 'object') return obj
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== '' && v !== undefined && v !== null)
  )
}

/**
 * 
 * @param {Object} obj - Objeto do qual remover propriedades vazias
 * @returns {Object} - Objeto limpo ou { vazio: 'vazio' } se estiver vazio
 * 
 * @example
 * limparFiltros({ a: 'valor', b: '' }) // { a: 'valor' }
 * limparFiltros({ ULTIMA_CONEXAO_INICIO: '', ULTIMA_CONEXAO_FINAL: '' }) // { vazio: 'vazio' }
 * limparFiltros({ STATUS: 'Pago', ULTIMA_CONEXAO_INICIO: '' }) // { STATUS: 'Pago' }
 */
export function limparFiltros(obj) {
  // Se obj for null, undefined ou não for objeto, retorna objeto vazio
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return { vazio: 'vazio' }
  }
  
  // Remove propriedades vazias
  const filtrosLimpos = removeEmptyProperties(obj)
  
  // Se o objeto estiver vazio ou só tiver a propriedade 'vazio', retorna objeto vazio
  const keys = Object.keys(filtrosLimpos)
  if (keys.length === 0 || (keys.length === 1 && keys[0] === 'vazio' && filtrosLimpos.vazio === 'vazio')) {
    return { vazio: 'vazio' }
  }
  
  return filtrosLimpos
}
